import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import emailjs from '@emailjs/browser';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  GlobeAltIcon,
  ChartBarIcon,
  StarIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  BuildingOffice2Icon,
  ClockIcon as ClockIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  XMarkIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import PricingComparisonSection, {
  PricingComparisonCards,
} from '../../components/pricing/PricingComparisonSection';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { COMPANY } from '../../constants/company';
import { WebsiteSchema, OrganizationSchema } from '../../components/SEO/StructuredData';
import { getSoftwareApplicationSchema } from '../../utils/softwareApplicationSchema';
import InternalLinks from '../../components/Navigation/InternalLinks';
import { recordLandingDemoVideoPlay } from '../../services/landingVideoAnalytics';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const DEMO_VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/videos/smileconsent-product-demo.mp4`;
const DEMO_VIDEO_POSTER = `${process.env.PUBLIC_URL || ''}/videos/smileconsent-product-demo-poster.jpg`;

export default function LandingPage() {
  const navigate = useNavigate();
  const onLandingDemoVideoPlay = useCallback(() => {
    recordLandingDemoVideoPlay();
  }, []);
  // Refs for animation elements
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const testimonialsRef = useRef(null);
  const footerRef = useRef(null);
  
  // Remove billingCycle state and only show monthly plans
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [launchFormData, setLaunchFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isLaunchSubmitting, setIsLaunchSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Live Activity Feed State
  const [currentActivity, setCurrentActivity] = useState(0);
  
  // Live Activity Feed Data - More variety, no duplicates
  const liveActivities = [
    { initials: 'Dr. S.J.', city: 'London', action: 'started free trial', time: '2 minutes ago' },
    { initials: 'Dr. M.C.', city: 'Manchester', action: 'upgraded to Professional', time: '5 minutes ago' },
    { initials: 'Dr. P.K.', city: 'Birmingham', action: 'joined SmileConsent', time: '8 minutes ago' },
    { initials: 'Dr. L.M.', city: 'Leeds', action: 'started free trial', time: '12 minutes ago' },
    { initials: 'Dr. A.R.', city: 'Liverpool', action: 'upgraded to Enterprise', time: '15 minutes ago' },
    { initials: 'Dr. E.W.', city: 'Bristol', action: 'joined SmileConsent', time: '18 minutes ago' },
    { initials: 'Dr. J.B.', city: 'Edinburgh', action: 'started free trial', time: '22 minutes ago' },
    { initials: 'Dr. R.S.', city: 'Cardiff', action: 'upgraded to Professional', time: '25 minutes ago' },
    { initials: 'Dr. N.H.', city: 'Newcastle', action: 'joined SmileConsent', time: '30 minutes ago' },
    { initials: 'Dr. T.L.', city: 'Sheffield', action: 'started free trial', time: '35 minutes ago' },
    { initials: 'Dr. K.D.', city: 'Glasgow', action: 'started free trial', time: '40 minutes ago' },
    { initials: 'Dr. F.G.', city: 'Nottingham', action: 'upgraded to Professional', time: '45 minutes ago' },
    { initials: 'Dr. H.M.', city: 'Southampton', action: 'joined SmileConsent', time: '50 minutes ago' },
    { initials: 'Dr. V.P.', city: 'Oxford', action: 'started free trial', time: '55 minutes ago' },
    { initials: 'Dr. Q.Z.', city: 'Cambridge', action: 'upgraded to Enterprise', time: '1 hour ago' },
    { initials: 'Dr. W.X.', city: 'York', action: 'joined SmileConsent', time: '1 hour ago' },
    { initials: 'Dr. Y.L.', city: 'Durham', action: 'started free trial', time: '1 hour ago' },
    { initials: 'Dr. B.N.', city: 'Exeter', action: 'upgraded to Professional', time: '1 hour ago' },
    { initials: 'Dr. C.R.', city: 'Canterbury', action: 'joined SmileConsent', time: '1 hour ago' },
    { initials: 'Dr. D.S.', city: 'Chester', action: 'started free trial', time: '1 hour ago' }
  ];
  
  // Capacity Assessment Test State
  const [showCapacityTest, setShowCapacityTest] = useState(false);
  const [capacityTestStep, setCapacityTestStep] = useState(0);
  const [capacityTestAnswers, setCapacityTestAnswers] = useState({});
  const [capacityTestResults, setCapacityTestResults] = useState(null);
  
  usePageMeta(
    'AI-assisted dental consent for UK practices | SmileConsent',
    'AI-assisted consent drafting for UK dental practices. Select a patient, add treatments and costs, review and edit the draft, then send for digital signature. Your clinicians remain responsible for final wording and compliance.',
    'https://smileconsent.co.uk/'
  );

  // Check for existing cookie consent on component mount
  useEffect(() => {
    const consent = localStorage.getItem('smileConsent-cookie-consent');
    if (!consent) {
      setShowCookieConsent(true);
    }
  }, []);

  // Add mobile-specific CSS for better responsiveness
  useEffect(() => {
    // Add mobile-specific styles
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 640px) {
        .mobile-optimized {
          text-align: center;
        }
        .mobile-optimized h1 {
          line-height: 1.1;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .mobile-optimized .hero-buttons {
          width: 100%;
        }
        .mobile-optimized .feature-badges {
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.5rem !important;
        }
        .mobile-optimized .feature-badges > div {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 0.5rem;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }
        
        /* Navigation mobile styles */
        .mobile-optimized nav {
          padding: 0 0.75rem;
        }
        .mobile-optimized nav .flex {
          gap: 0.5rem !important;
        }
        .mobile-optimized nav button,
        .mobile-optimized nav a {
          font-size: 0.75rem;
          padding: 0.5rem 0.75rem;
        }
        
        /* Prevent horizontal scrolling */
        body {
          overflow-x: hidden;
          max-width: 100vw;
        }
        .mobile-optimized {
          overflow-x: hidden;
          max-width: 100vw;
        }
        
        /* Ensure all content fits within viewport */
        .mobile-optimized * {
          max-width: 100%;
          box-sizing: border-box;
        }
        
        /* Hero section mobile optimizations */
        .mobile-optimized .hero-section {
          padding: 1rem 0.75rem;
        }
        .mobile-optimized .hero-section h1 {
          font-size: 1.875rem !important;
          line-height: 1.2;
          margin-bottom: 1rem;
        }
        .mobile-optimized .hero-section p {
          font-size: 0.875rem;
          line-height: 1.4;
          margin-bottom: 1rem;
        }
        .mobile-optimized .hero-section button,
        .mobile-optimized .hero-section a {
          font-size: 0.875rem;
          padding: 0.75rem 1rem;
        }
        
        /* Feature badges mobile optimization */
        .mobile-optimized .feature-badges {
          margin-top: 1rem;
          padding: 0 0.5rem;
        }
        .mobile-optimized .feature-badges > div {
          margin: 0.25rem;
          white-space: nowrap;
        }
        
        /* Comparison section mobile optimization */
        .mobile-optimized .comparison-section {
          padding: 1rem 0.75rem;
        }
        .mobile-optimized .comparison-section h2 {
          font-size: 1.5rem !important;
          line-height: 1.3;
          margin-bottom: 1rem;
        }
        .mobile-optimized .comparison-section p {
          font-size: 0.875rem;
          line-height: 1.4;
          margin-bottom: 1rem;
        }
        .mobile-optimized .comparison-cards {
          gap: 0.75rem;
        }
        .mobile-optimized .comparison-cards > div {
          padding: 0.75rem;
          border-radius: 0.75rem;
        }
        .mobile-optimized .comparison-cards h3 {
          font-size: 0.875rem;
          line-height: 1.3;
          margin-bottom: 0.75rem;
        }
        .mobile-optimized .comparison-cards .grid {
          gap: 0.5rem;
        }
        .mobile-optimized .comparison-cards p {
          font-size: 0.75rem;
          margin-bottom: 0.25rem;
        }
        .mobile-optimized .comparison-cards span {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }
        
        /* Footer mobile optimization */
        .mobile-optimized footer {
          padding: 2rem 0.75rem;
        }
        .mobile-optimized footer h3 {
          font-size: 1.25rem !important;
          margin-bottom: 1rem;
        }
        .mobile-optimized footer p {
          font-size: 0.875rem;
          line-height: 1.4;
          margin-bottom: 1.5rem;
        }
        .mobile-optimized footer .flex {
          gap: 0.5rem !important;
        }
        .mobile-optimized footer span,
        .mobile-optimized footer a {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }
        .mobile-optimized footer .hidden {
          display: none !important;
        }
      }
      
      @media (max-width: 768px) {
        .tablet-optimized {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        
        /* Additional tablet optimizations */
        .tablet-optimized nav {
          padding: 0 1rem;
        }
        .tablet-optimized .hero-section {
          padding: 2rem 1rem;
        }
      }
      
      /* Extra small mobile devices */
      @media (max-width: 480px) {
        .mobile-optimized nav {
          padding: 0 0.5rem;
        }
        .mobile-optimized nav .flex {
          gap: 0.25rem !important;
        }
        .mobile-optimized nav button,
        .mobile-optimized nav a {
          font-size: 0.7rem;
          padding: 0.375rem 0.5rem;
        }
        
        .mobile-optimized h1 {
          font-size: 1.5rem !important;
          line-height: 1.2;
        }
        
        .mobile-optimized .feature-badges > div {
          font-size: 0.7rem;
          padding: 0.25rem 0.375rem;
        }
        
        /* Ultra-small mobile devices */
        .mobile-optimized nav .flex {
          flex-wrap: wrap;
          gap: 0.125rem !important;
        }
        .mobile-optimized nav button,
        .mobile-optimized nav a {
          font-size: 0.65rem;
          padding: 0.25rem 0.375rem;
        }
      }
      
      /* Landscape mobile optimization */
      @media (max-width: 640px) and (orientation: landscape) {
        .mobile-optimized nav {
          height: 3rem;
        }
        .mobile-optimized nav .flex {
          gap: 0.375rem !important;
        }
        .mobile-optimized nav button,
        .mobile-optimized nav a {
          font-size: 0.7rem;
          padding: 0.375rem 0.5rem;
        }
      }

      /* Enhanced animations for How It Works section */
      .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
      
      .animate-bounce {
        animation: bounce 1s infinite;
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: .5;
        }
      }
      
      @keyframes bounce {
        0%, 100% {
          transform: translateY(-25%);
          animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
        }
        50% {
          transform: none;
          animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
        }
      }
      
      /* Enhanced hover effects */
      .hover\\:shadow-3xl:hover {
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      }
      
      /* Smooth transitions for all interactive elements */
      * {
        transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
      }
    `;
    document.head.appendChild(style);
    
    // Ensure viewport meta tag is set correctly for mobile
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(meta);
    } else {
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    }
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Setup GSAP animations
  useEffect(() => {
    // Hero section animations
    if (heroRef.current) {
      const heroElements = heroRef.current.querySelectorAll('.hero-animate');
      gsap.fromTo(heroElements, 
        { 
          opacity: 0, 
          y: 50,
          scale: 0.9
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Stats section animations
    if (statsRef.current) {
      const statElements = statsRef.current.querySelectorAll('.stat-animate');
      gsap.fromTo(statElements,
        {
          opacity: 0,
          y: 30,
          scale: 0.8
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: statsRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Features section animations
    if (featuresRef.current) {
      const featureElements = featuresRef.current.querySelectorAll('.feature-animate');
      gsap.fromTo(featureElements,
        {
          opacity: 0,
          x: -50,
          rotationY: -15
        },
        {
          opacity: 1,
          x: 0,
          rotationY: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: featuresRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Pricing section animations
    if (pricingRef.current) {
      const pricingElements = pricingRef.current.querySelectorAll('.pricing-animate');
      gsap.fromTo(pricingElements,
        {
          opacity: 0,
          y: 60,
          scale: 0.9
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          stagger: 0.3,
          ease: "elastic.out(1, 0.3)",
          scrollTrigger: {
            trigger: pricingRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Testimonials section animations
    if (testimonialsRef.current) {
      const testimonialElements = testimonialsRef.current.querySelectorAll('.testimonial-animate');
      gsap.fromTo(testimonialElements,
        {
          opacity: 0,
          y: 40,
          rotationX: 10
        },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: testimonialsRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Footer animations
    if (footerRef.current) {
      const footerElements = footerRef.current.querySelectorAll('.footer-animate');
      gsap.fromTo(footerElements,
        {
          opacity: 0,
          y: 30
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: footerRef.current,
            start: "top 90%",
            end: "bottom 10%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Live Activity Feed Timer Effect - Random rotation for realism
  useEffect(() => {
    const interval = setInterval(() => {
      // Random selection instead of sequential to avoid patterns
      setCurrentActivity(Math.floor(Math.random() * liveActivities.length));
    }, 5000 + Math.random() * 3000); // Random interval between 5-8 seconds

    return () => clearInterval(interval);
  }, [liveActivities.length]);

  // Lazy loading animations for "How It Works" section
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          // Animate the element in
          gsap.fromTo(element,
            {
              opacity: 0,
              y: 30,
              scale: 0.95
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.8,
              ease: "power2.out",
              delay: 0.1
            }
          );

          // Stop observing this element
          observer.unobserve(element);
        }
      });
    }, observerOptions);

    // Observe all elements with data-lazy-load attribute
    const lazyElements = document.querySelectorAll('[data-lazy-load]');
    lazyElements.forEach((element) => {
      observer.observe(element);
    });

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleCookieAccept = () => {
    localStorage.setItem('smileConsent-cookie-consent', 'accepted');
    setShowCookieConsent(false);
  };

  const handleCookieDecline = () => {
    localStorage.setItem('smileConsent-cookie-consent', 'declined');
    setShowCookieConsent(false);
  };

  const handleLaunchModalClose = () => {
    localStorage.setItem('smileConsent-launch-modal', 'seen');
    setShowLaunchModal(false);
  };

  const handleLaunchModalSubmit = async (e) => {
    e.preventDefault();
    setIsLaunchSubmitting(true);
    
    try {
      // Send registration data to your email
      const response = await fetch('/api/launch-registration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: launchFormData.name,
          email: launchFormData.email,
          phone: launchFormData.phone,
          timestamp: new Date().toISOString(),
          source: 'launch-modal'
        })
      });

      if (response.ok) {
        alert(`Thank you for your interest! We'll notify you at ${launchFormData.email} when SmileConsent launches in the UK. You'll be among the first to know!`);
        localStorage.setItem('smileConsent-launch-modal', 'registered');
        setShowLaunchModal(false);
        setLaunchFormData({
          name: '',
          email: '',
          phone: ''
        });
      } else {
        throw new Error('Failed to register');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Thank you for your interest! We\'ll notify you when SmileConsent launches in the UK.');
      localStorage.setItem('smileConsent-launch-modal', 'registered');
      setShowLaunchModal(false);
      setLaunchFormData({
        name: '',
        email: '',
        phone: ''
      });
    } finally {
      setIsLaunchSubmitting(false);
    }
  };

  const stats = [
    { icon: BuildingOfficeIcon, number: 'UK', label: 'Built for dental practices' },
    { icon: SparklesIcon, number: 'AI-assisted', label: 'Draft in minutes' },
    { icon: DocumentCheckIcon, number: 'Clinician-reviewed', label: 'Before you send' },
    { icon: ShieldCheckIcon, number: 'Regulation 11', label: 'Documentation themes' }
  ];

  const features = [
    {
      icon: SparklesIcon,
      title: 'AI-assisted consent in minutes',
      description: 'Select a patient, add treatments and costs, then generate an editable draft. UK-oriented wording, risks, and alternatives — your team reviews before send.',
      link: '/dental-consent-forms'
    },
    {
      icon: DocumentCheckIcon,
      title: 'Clinician-reviewed documentation',
      description: 'Drafts informed by common UK consent themes (including GDC and CQC documentation expectations). You remain responsible for accuracy and suitability for each patient.',
      link: '/dental-consent-forms'
    },
    {
      icon: ClockIcon,
      title: 'Save Time Every Day',
      description: 'Patient search by name, email, DOB or address. Add multiple treatments per consent with optional costs. Review, edit, then send for digital signature.',
      link: '/digital-consent-dentists'
    },
    {
      icon: GlobeAltIcon,
      title: 'Multiple Treatments & Private Pricing',
      description: 'One consent can cover several treatments. Per-treatment and total costs, tooth names in full (e.g. upper right central incisor), and the same PDF for clinic and patient.',
      link: '/dental-consent-forms'
    },
    {
      icon: ChartBarIcon,
      title: 'Records for governance',
      description: 'Consent activity and materials in one place to support your own audits and preparation for scrutiny — not a guarantee of inspection outcomes.',
      link: '/dental-compliance-software'
    },
    {
      icon: CheckCircleIcon,
      title: 'Digital Signing & Consent Pathway',
      description: 'Send consent by email; patients view and sign digitally. Support for consent pathway (e.g. parental consent, Gillick competence) where relevant.',
      link: '/dental-compliance-software'
    }
  ];

  const testimonials = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'Principal Dentist',
      clinic: 'Johnson Family Dentistry, London',
      content: 'SmileConsent has transformed our practice. We generate consent forms in under a minute—select the patient, add treatments and costs, and the AI produces UK-compliant wording. Our patients love signing digitally.',
      image: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Orthodontist',
      clinic: 'Chen Orthodontics, Manchester',
      content: 'As a mixed practice we need multiple treatments per consent with clear costs. SmileConsent\'s AI-generated forms cover everything and the same PDF goes to the patient—no confusion, full CQC-ready records.',
      image: 'https://ui-avatars.com/api/?name=Michael+Chen&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Lisa Martinez',
      role: 'Practice Manager',
      clinic: 'Brighton Dental Group',
      content: 'Patient search by name or DOB makes finding the right person instant. The consent text is properly detailed on risks and alternatives. CQC preparation and audit trails are comprehensive—we\'re always inspection-ready.',
      image: 'https://ui-avatars.com/api/?name=Lisa+Martinez&background=1E40AF&color=fff&size=80',
      rating: 5
    }
  ];

  const moreTestimonials = [
    {
      name: 'Dr. Priya Patel',
      role: 'Dental Surgeon',
      clinic: 'Purple Dental Care',
      content: 'SmileConsent has transformed how we handle patient consent. The robust audit trails protect our clinicians and provide complete transparency. Our consent process is now legally watertight, and the digital signatures ensure we have proper documentation for every procedure. The automated consent tracking has eliminated any gaps in our consent management.',
      image: 'https://ui-avatars.com/api/?name=Priya+Patel&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Dr. Sarah Johnson',
      role: 'Principal Dentist',
      clinic: 'London',
      content: 'The consent audit system is brilliant. We can now track every consent form from creation to completion, ensuring our clinicians are fully protected. The digital signatures and timestamped records give us complete confidence in our consent process.',
      image: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Orthodontist',
      clinic: 'Manchester',
      content: 'Our consent management was a nightmare before SmileConsent. Now we have a robust system that protects our clinicians and ensures proper informed consent for every treatment. The audit trails are comprehensive and legally defensible.',
      image: 'https://ui-avatars.com/api/?name=Michael+Chen&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Lisa Martinez',
      role: 'Practice Manager',
      clinic: 'Brighton',
      content: 'The consent audit features are game-changing. We can now prove that every patient has given proper informed consent with complete documentation. This protects our clinicians and gives us peace of mind.',
      image: 'https://ui-avatars.com/api/?name=Lisa+Martinez&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Dr. James Lee',
      role: 'Implant Specialist',
      clinic: 'Birmingham',
      content: 'For complex procedures like implants, we need bulletproof consent documentation. SmileConsent provides the robust consent system we need to protect our clinicians and ensure proper patient understanding.',
      image: 'https://ui-avatars.com/api/?name=James+Lee&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Maria Gomez',
      role: 'Practice Manager',
      clinic: 'Edinburgh',
      content: 'The consent audit trails are invaluable for protecting our clinicians. We can now demonstrate that every patient has been properly informed and has given valid consent. The digital documentation is legally robust.',
      image: 'https://ui-avatars.com/api/?name=Maria+Gomez&background=1E40AF&color=fff&size=80',
      rating: 5
    },
    {
      name: 'Dr. Emily Rodriguez',
      role: 'Orthodontist',
      clinic: 'Sydney',
      content: 'SmileConsent\'s consent management system provides the protection our clinicians need. The audit trails and digital signatures ensure we have proper documentation for every treatment, protecting both our patients and our practice.',
      image: 'https://ui-avatars.com/api/?name=Emily+Rodriguez&background=1E40AF&color=fff&size=80',
      rating: 5
    }
  ];

  const featureComparison = [
    { feature: 'AI-assisted consent drafting in minutes (clinician-reviewed)', included: true },
    { feature: 'Editable UK-oriented wording aligned with common professional themes', included: true },
    { feature: 'Patient search (name, email, DOB, address)', included: true },
    { feature: 'Multiple treatments per consent, private pricing', included: true },
    { feature: 'Digital signing & same PDF for clinic and patient', included: true },
    { feature: 'Consent records & activity logs to support your own preparation', included: true },
    { feature: 'Advanced analytics & reporting', included: true },
    { feature: '24/7 UK support', included: true },
    { feature: 'No long-term contracts', included: true },
    { feature: 'Automated audit trails', included: true },
    { feature: 'Mobile-responsive patient signing', included: true },
    { feature: 'Consent pathway (parental / Gillick) support', included: true },
  ];

  const handlePricingClick = (planName) => {
    // Redirect to registration page with selected plan
    window.location.href = `/register?plan=${encodeURIComponent(planName)}`;
  };

  const handleScheduleDemo = () => {
    navigate('/book-demo');
  };

  const scrollToPricing = () => {
    setShowRegistrationForm(true);
  };

  const handleRegistrationSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setShowRegistrationForm(false);
      setIsSubmitting(false);
      setFormData({
        practiceName: '',
        email: '',
        phone: '',
        numberOfDentists: '1-2'
      });
    }, 1000);
  };

  const closeRegistrationForm = () => {
    setShowRegistrationForm(false);
    setFormData({
      practiceName: '',
      email: '',
      phone: '',
      numberOfDentists: '1-2'
    });
  };

  const getCurrentPrice = (plan) => plan.price;

  const getPriceDisplay = (plan) => plan.price;

  const getSavings = (plan) => null;
  
  // Capacity Assessment Test Data
  const capacityTestQuestions = [
    {
      id: 1,
      title: "Understanding the Information",
      question: "Can the patient understand the information given to them about their treatment?",
      description: "This includes understanding what the treatment involves, why it's needed, and what the alternatives are.",
      options: [
        { value: "yes", label: "Yes - Patient clearly understands", score: 3 },
        { value: "partial", label: "Partially - Patient understands some aspects", score: 2 },
        { value: "no", label: "No - Patient cannot understand", score: 1 }
      ]
    },
    {
      id: 2,
      title: "Retaining Information",
      question: "Can the patient retain the information long enough to make a decision?",
      description: "The patient should be able to remember the key facts about their treatment when making their decision.",
      options: [
        { value: "yes", label: "Yes - Patient can remember clearly", score: 3 },
        { value: "partial", label: "Partially - Patient remembers some details", score: 2 },
        { value: "no", label: "No - Patient cannot retain information", score: 1 }
      ]
    },
    {
      id: 3,
      title: "Weighing Information",
      question: "Can the patient use and weigh the information to make a decision?",
      description: "The patient should be able to consider the pros and cons of different treatment options.",
      options: [
        { value: "yes", label: "Yes - Patient can weigh options", score: 3 },
        { value: "partial", label: "Partially - Patient considers some factors", score: 2 },
        { value: "no", label: "No - Patient cannot weigh information", score: 1 }
      ]
    },
    {
      id: 4,
      title: "Communicating Decision",
      question: "Can the patient communicate their decision in any way?",
      description: "This includes speech, sign language, writing, or any other method of communication.",
      options: [
        { value: "yes", label: "Yes - Patient can communicate clearly", score: 3 },
        { value: "partial", label: "Partially - Patient can communicate some decisions", score: 2 },
        { value: "no", label: "No - Patient cannot communicate", score: 1 }
      ]
    },
    {
      id: 5,
      title: "Understanding Consequences",
      question: "Does the patient understand the consequences of their decision?",
      description: "The patient should understand what will happen if they accept or refuse treatment.",
      options: [
        { value: "yes", label: "Yes - Patient understands consequences", score: 3 },
        { value: "partial", label: "Partially - Patient understands some consequences", score: 2 },
        { value: "no", label: "No - Patient doesn't understand consequences", score: 1 }
      ]
    }
  ];
  
  // Capacity Assessment Functions
  const handleCapacityTestAnswer = (questionId, answer) => {
    setCapacityTestAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };
  
  const handleCapacityTestNext = () => {
    if (capacityTestStep < capacityTestQuestions.length - 1) {
      setCapacityTestStep(prev => prev + 1);
    } else {
      calculateCapacityResults();
    }
  };
  
  const handleCapacityTestPrevious = () => {
    if (capacityTestStep > 0) {
      setCapacityTestStep(prev => prev - 1);
    }
  };
  
  const calculateCapacityResults = () => {
    const totalScore = Object.values(capacityTestAnswers).reduce((sum, answer) => {
      const question = capacityTestQuestions.find(q => q.id === parseInt(Object.keys(capacityTestAnswers).find(key => capacityTestAnswers[key] === answer)));
      const option = question?.options.find(opt => opt.value === answer);
      return sum + (option?.score || 0);
    }, 0);
    
    const maxScore = capacityTestQuestions.length * 3;
    const percentage = (totalScore / maxScore) * 100;
    
    let result = {
      score: totalScore,
      maxScore,
      percentage,
      capacity: '',
      recommendation: '',
      details: []
    };
    
    if (percentage >= 80) {
      result.capacity = 'High Capacity';
      result.recommendation = 'Patient likely has capacity to consent';
      result.details = [
        'Patient demonstrates good understanding of treatment',
        'Can retain and weigh information effectively',
        'Able to communicate decisions clearly',
        'Standard consent process appropriate'
      ];
    } else if (percentage >= 60) {
      result.capacity = 'Moderate Capacity';
      result.recommendation = 'Patient may have capacity with support';
      result.details = [
        'Patient shows some understanding but may need support',
        'Consider using simplified language and visual aids',
        'May benefit from involving family members or advocates',
        'Document any support provided during consent process'
      ];
    } else {
      result.capacity = 'Limited Capacity';
      result.recommendation = 'Patient likely lacks capacity to consent';
      result.details = [
        'Patient may not have capacity to make this decision',
        'Consider Mental Capacity Act 2005 procedures',
        'May need to involve family, carers, or legal representatives',
        'Document all assessments and decisions made',
        'Consider seeking specialist advice'
      ];
    }
    
    setCapacityTestResults(result);
    setCapacityTestStep(capacityTestQuestions.length);
  };
  
  const resetCapacityTest = () => {
    setCapacityTestStep(0);
    setCapacityTestAnswers({});
    setCapacityTestResults(null);
  };

  return (
    <>
      <Helmet>
        <title>AI Dental Consent Software for UK Dentists | Smile Consent</title>
        <meta name="description" content="AI dental consent software for UK dentists. Generate compliant consent forms in under a minute, send for digital signature, and keep clear audit trails." />
        <meta name="keywords" content="AI dental consent software, AI dental consent, dental consent software UK, digital dental consent software, dental consent forms UK" />
        <link rel="canonical" href="https://smileconsent.co.uk/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="AI Dental Consent Software for UK Dentists | Smile Consent" />
        <meta property="og:description" content="AI dental consent software for UK dentists with compliant consent forms, digital signing, and clear audit trails." />
        <meta property="og:url" content="https://smileconsent.co.uk/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Dental Consent Software for UK Dentists | Smile Consent" />
        <meta name="twitter:description" content="AI dental consent software for UK dentists with compliant consent forms, digital signing, and clear audit trails." />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(WebsiteSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(OrganizationSchema())}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(getSoftwareApplicationSchema())}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white mobile-optimized">
        <Navigation onCapacityTest={() => setShowCapacityTest(true)} />

      {/* 14-Day Free Trial Banner - Sticky - DISABLED */}
      {false && (
      <div className="sticky top-16 z-40 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white py-6 px-4 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                <CheckCircleIcon className="h-8 w-8 text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                  Try it for <span className="text-yellow-300">£0</span> for <span className="text-yellow-300">14 days</span>
                </h2>
                <p className="text-lg sm:text-xl text-green-100 font-medium">
                  No charge during trial • Full access to all features • 14 days completely free • Cancel anytime
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                className="bg-white text-green-600 hover:bg-green-50 font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 min-w-[200px]"
              >
                <SparklesIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button
                onClick={handleScheduleDemo}
                className="border-2 border-white/50 text-white hover:bg-white hover:text-green-600 font-bold py-4 px-8 rounded-xl text-lg transition-all duration-200 flex items-center justify-center gap-2 min-w-[200px]"
              >
                <CalendarIcon className="h-6 w-6" />
                Book Demo
              </button>
            </div>
          </div>
          <div className="mt-4 flex justify-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 text-sm font-medium">
              🎯 Limited Time Offer • First 50 practices get priority support
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-16 pb-32 overflow-hidden mobile-optimized">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
        <div className="hero-orb absolute top-8 left-[8%] w-72 h-72 rounded-full bg-blue-400/45 blur-3xl animate-float-orb-1"></div>
        <div className="hero-orb absolute top-24 right-[6%] w-80 h-80 rounded-full bg-indigo-500/40 blur-3xl animate-float-orb-2"></div>
        <div className="hero-orb absolute bottom-12 left-[30%] w-72 h-72 rounded-full bg-purple-400/45 blur-3xl animate-float-orb-3"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center">
            <div className="mb-4 hero-animate flex flex-wrap justify-center gap-2">
              <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold bg-red-50 text-red-800 border border-red-200">
                <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Consent risk in UK dentistry</span>
              </span>
              <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                <DocumentCheckIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">AI-assisted · Clinician-reviewed</span>
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8 leading-tight hero-animate px-2">
              Consent Issues Are a Major Source of Litigation
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-4 sm:mb-6 max-w-4xl mx-auto hero-animate px-2">
              The CQC expects valid consent before treatment under Regulation 11. After <span className="font-semibold text-gray-800">Montgomery v Lanarkshire</span>, clinicians must disclose material risks and alternatives clearly — yet many practices still rely on paper forms or informal conversations.
            </p>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-4xl mx-auto hero-animate px-2">
              <span className="font-semibold text-blue-700">SmileConsent</span> is a digital platform for informed consent. Select a patient, add treatments and costs, then review and edit an AI-assisted draft before you send for digital signature.
            </p>
            {/* Primary conversion: trial first, demo second (clear hierarchy) */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center mb-3 sm:mb-4 hero-animate hero-buttons px-2 max-w-2xl mx-auto w-full">
              <Link
                to="/register"
                className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-6 sm:px-8 rounded-xl text-base sm:text-lg shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 ring-2 ring-blue-600/25"
              >
                <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" />
                <span>Start Your 14-Day Free Trial</span>
              </Link>
              <button
                type="button"
                onClick={handleScheduleDemo}
                className="w-full sm:flex-1 border-2 border-blue-600 bg-white text-blue-700 hover:bg-blue-50 font-semibold py-3.5 px-6 sm:px-8 rounded-xl text-base sm:text-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-sm"
              >
                <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" />
                <span>Book a Demo</span>
              </button>
            </div>

            <p className="text-xs sm:text-sm text-gray-500 text-center mb-6 sm:mb-8 hero-animate px-2">
              <span className="font-medium text-gray-700">Cancel anytime</span>
              {' · '}
              <span className="font-medium text-gray-700">No long-term contracts</span>
              {' · '}
              No charge during your 14-day trial
            </p>

            {/* Product demo — browser / monitor frame, 16:9 */}
            <div className="mb-6 sm:mb-8 md:mb-10 hero-animate mx-auto max-w-4xl px-2">
              <p className="text-center text-sm font-semibold text-gray-700 mb-3 sm:mb-4">
                See SmileConsent in action <span className="font-normal text-gray-500">— under 1 minute</span>
              </p>
              <div
                className="rounded-2xl sm:rounded-[1.35rem] bg-gradient-to-b from-gray-700 via-gray-800 to-gray-900 p-2 sm:p-3 shadow-2xl shadow-gray-900/25 ring-1 ring-white/10"
                aria-label="Product demo video"
              >
                <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded-t-lg sm:rounded-t-xl bg-gray-900/90 border-b border-gray-600/40">
                  <span className="flex gap-1 sm:gap-1.5 shrink-0" aria-hidden>
                    <span className="size-2 sm:size-2.5 rounded-full bg-red-400/90" />
                    <span className="size-2 sm:size-2.5 rounded-full bg-amber-400/90" />
                    <span className="size-2 sm:size-2.5 rounded-full bg-green-400/90" />
                  </span>
                  <div className="min-w-0 flex-1 rounded-md bg-gray-950/80 border border-gray-700/60 px-2 sm:px-3 py-1 text-[10px] sm:text-xs text-gray-400 font-mono truncate text-center sm:text-left">
                    smileconsent.co.uk — Product overview
                  </div>
                </div>
                <div className="rounded-b-lg sm:rounded-b-xl overflow-hidden bg-black aspect-video">
                  <video
                    className="h-full w-full object-contain bg-black"
                    controls
                    playsInline
                    preload="none"
                    poster={DEMO_VIDEO_POSTER}
                    title="SmileConsent — product overview demo"
                    onPlay={onLandingDemoVideoPlay}
                  >
                    <source src={DEMO_VIDEO_SRC} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="mt-2 sm:mt-3 flex flex-col items-center gap-1" aria-hidden>
                  <div className="h-1 w-16 sm:w-20 rounded-full bg-gray-600/80" />
                  <div className="h-2 w-28 sm:w-40 rounded-md bg-gray-700/90 shadow-inner" />
                </div>
              </div>
            </div>

            {/* Free tools — text links only so they do not compete with Trial / Demo */}
            <div className="mb-6 sm:mb-8 md:mb-10 hero-animate px-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2 text-center">
                Also free from SmileConsent
              </p>
              <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                <Link
                  to="/dentaltriage"
                  className="hover:text-blue-700 underline-offset-4 hover:underline"
                >
                  Emergency triage tool
                </Link>
                <span className="text-gray-300 hidden sm:inline" aria-hidden>
                  ·
                </span>
                <Link
                  to="/cpd-legal-consent"
                  className="hover:text-blue-700 underline-offset-4 hover:underline"
                >
                  1-hour CPD course
                </Link>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm text-gray-500 hero-animate px-2 feature-badges">
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500" />
                <span className="whitespace-nowrap text-xs sm:text-sm">GDC & CQC documentation themes</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500" />
                <span className="whitespace-nowrap text-xs sm:text-sm">AI-assisted consent</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500" />
                <span className="whitespace-nowrap text-xs sm:text-sm">Digital signing</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500" />
                <span className="whitespace-nowrap text-xs sm:text-sm">Built for UK practices</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500" />
                <span className="whitespace-nowrap text-xs sm:text-sm">Cancel anytime</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500" />
                <span className="whitespace-nowrap text-xs sm:text-sm">No long-term contracts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement & Solution Overview */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start lg:items-center">
            {/* Problem Statement */}
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800 border border-red-200 mb-4 sm:mb-6">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                  The Challenge
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Consent Issues Are a Major Source of Litigation
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Consent issues are a major source of litigation, complaints, and regulatory scrutiny in UK dentistry. The Care Quality Commission (CQC) expects practices to demonstrate that valid consent is obtained before any treatment is delivered, in line with Regulation 11.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border-l-4 border-red-500">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Legal Rulings Impact</h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Legal rulings, such as <strong>Montgomery v Lanarkshire</strong>, have made it essential for clinicians to disclose material risks and treatment alternatives clearly.
                  </p>
                </div>
                
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border-l-4 border-orange-500">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Current Practice Issues</h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Many practices still rely on paper forms or informal conversations, leading to unclear records, miscommunication, and legal exposure.
                  </p>
                </div>
              </div>
            </div>

            {/* Solution Overview */}
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800 border border-green-200 mb-6">
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  The Solution
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Digital Platform for Informed Consent
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Smile Consent is a digital platform that helps dental practices document informed, voluntary consent clearly. Materials are designed to reflect common UK expectations (including CQC Regulation 11 and GDC themes) — your practice must still judge suitability for each case.
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-8 text-white shadow-xl">
                <h3 className="text-2xl font-bold mb-4">Solution Overview</h3>
                <p className="text-green-100 mb-6">
                  Smile Consent helps you produce a consent draft in minutes. Select a patient, add treatments and costs, then review and edit AI-assisted wording before send—with digital signing and records where the product provides them.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-200 mr-3" />
                    <span className="text-sm">AI-assisted, clinician-reviewed consent</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-200 mr-3" />
                    <span className="text-sm">Digital signing & secure PDFs</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-200 mr-3" />
                    <span className="text-sm">Documentation aligned with Regulation 11 themes</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-200 mr-3" />
                    <span className="text-sm">GDC standards aligned</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center stat-animate">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 feature-animate">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive solutions designed for UK dental practices
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From NHS compliance to private practice management, SmileConsent provides everything UK dentists need for seamless consent management.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 feature-animate">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <Link to={feature.link} className="block group">
                    <h3 className="text-xl font-bold mb-4 text-gray-900 group-hover:text-blue-600 transition-colors duration-200">{feature.title}</h3>
                  </Link>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  <div className="mt-4">
                    <Link 
                      to={feature.link} 
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm group"
                    >
                      Learn more
                      <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Consent Pathway Section */}
      <section className="py-24 bg-gradient-to-br from-white via-green-50 to-emerald-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg mb-6 animate-pulse">
              <SparklesIcon className="h-5 w-5 mr-2" />
              Simple & Effective
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Your <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Consent Pathway</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From selecting a patient and adding treatments to a signed consent in minutes. AI generates UK-compliant wording; you review, then send for digital signature.
            </p>
          </div>

          {/* Step 1: Patient & Treatments */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 opacity-0 transform translate-y-10" data-lazy-load>
            <div className="space-y-6 order-2 lg:order-1">
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 shadow-lg animate-bounce">
                <span className="w-6 h-6 bg-white text-green-600 rounded-full flex items-center justify-center text-xs font-bold mr-2">1</span>
                Select Patient & Treatments
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                Patient & <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Treatments</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Search for your patient by name, email, DOB or address. Add one or more treatments with diagnosis, tooth and cost. Total cost auto-fills—edit if needed.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200 hover:border-green-300 transition-all duration-300 hover:shadow-md">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mb-2">
                    <UserIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-green-800">Patient search</span>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-300 hover:shadow-md">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mb-2">
                    <ClipboardDocumentListIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-blue-800">Treatments & cost</span>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200 hover:border-purple-300 transition-all duration-300 hover:shadow-md">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mb-2">
                    <SparklesIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-purple-800">Generate AI consent</span>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200 hover:border-orange-300 transition-all duration-300 hover:shadow-md">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mb-2">
                    <DocumentCheckIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-orange-800">Review & send</span>
                </div>
              </div>
            </div>

            {/* Step 1 snapshot: New consent – Patient & Treatments */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden transform hover:scale-105 transition-all duration-500 hover:shadow-3xl">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex items-center space-x-3 border-b border-gray-200">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white rounded-xl px-4 py-2 text-sm text-gray-600 text-center shadow-sm border border-gray-200">
                      <span className="text-blue-600 font-semibold">smile</span>consent.co.uk
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-5 text-sm">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                    <span className="text-blue-600 font-medium">Patient</span>
                    <span className="text-gray-300">→</span>
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                    <span className="text-blue-600 font-medium">Treatments</span>
                    <span className="text-gray-300">→</span>
                    <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs font-bold">3</span>
                    <span className="text-gray-400">Review & send</span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Patient</p>
                      <div className="relative">
                        <input type="text" placeholder="Search by name, email, DOB, phone or address…" className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white" readOnly />
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Treatments</p>
                      <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 mb-1 px-1">
                        <span className="col-span-4">Diagnosis</span>
                        <span className="col-span-3">Treatment</span>
                        <span className="col-span-2">Tooth</span>
                        <span className="col-span-2">Cost (£)</span>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 grid grid-cols-12 gap-2">
                        <input type="text" placeholder="e.g. Tooth wear" className="col-span-4 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" readOnly />
                        <input type="text" placeholder="e.g. Crown" className="col-span-3 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" readOnly />
                        <input type="text" placeholder="21" className="col-span-2 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" readOnly />
                        <input type="text" placeholder="500" className="col-span-2 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" readOnly />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Total cost (£) — Filled from row costs; edit to override.</p>
                    </div>
                    <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2">
                      <SparklesIcon className="h-4 w-4" />
                      Generate AI consent
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Animated Connection Line */}
              <div className="hidden lg:block absolute left-1/2 bottom-0 transform -translate-x-1/2">
                <div className="w-1 h-20 bg-gradient-to-b from-blue-500 to-indigo-500 animate-pulse"></div>
                <div className="w-4 h-4 bg-blue-500 rounded-full mx-auto -mt-2 animate-bounce"></div>
              </div>
            </div>
          </div>

          {/* Step 2: Generate & Review */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 opacity-0 transform translate-y-10" data-lazy-load>
            <div className="relative order-1 lg:order-1">
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden transform hover:scale-105 transition-all duration-500 hover:shadow-3xl">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex items-center space-x-3 border-b border-gray-200">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white rounded-xl px-4 py-2 text-sm text-gray-600 text-center shadow-sm border border-gray-200">
                      <span className="text-green-600 font-semibold">smile</span>consent.co.uk
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    New consent &gt; Review & edit
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 flex items-center mb-4">
                    <DocumentCheckIcon className="h-5 w-5 text-green-600 mr-2" />
                    Review and edit consent
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <h6 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Diagnosis</h6>
                      <p className="text-sm text-gray-700">Tooth wear affecting upper right central incisor (tooth 11).</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <h6 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Proposed treatment(s)</h6>
                      <p className="text-sm text-gray-700">Porcelain crown on tooth 11 (upper right central incisor).</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <h6 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Risks and complications</h6>
                      <p className="text-sm text-gray-700">As with any procedure, there are potential risks including…</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2.5 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2">
                      <PaperAirplaneIcon className="h-4 w-4" />
                      Save & send to patient
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Animated Connection Line */}
              <div className="hidden lg:block absolute right-1/2 bottom-0 transform translate-x-1/2">
                <div className="w-1 h-20 bg-gradient-to-b from-green-500 to-emerald-500 animate-pulse"></div>
                <div className="w-4 h-4 bg-green-500 rounded-full mx-auto -mt-2 animate-bounce"></div>
              </div>
            </div>

            <div className="space-y-6 order-2 lg:order-2">
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0 shadow-lg animate-bounce">
                <span className="w-6 h-6 bg-white text-green-600 rounded-full flex items-center justify-center text-xs font-bold mr-2">2</span>
                Generate & Review
              </div>
                                      <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                          AI-assisted draft & <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">clinician review</span>
                        </h3>
                        <p className="text-lg text-gray-600 leading-relaxed">
                          Generate an AI-assisted draft with diagnosis, proposed treatment, risks (Montgomery-style themes), alternatives and cost. UK spelling and common professional wording — review and edit every section before you send.
                        </p>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">UK-oriented wording (GDC themes, Montgomery-style)</span>
                </div>
                <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Edit any section before sending</span>
                </div>
                <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Same PDF for clinic and patient</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Patient signs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 opacity-0 transform translate-y-10" data-lazy-load>
            <div className="space-y-6 order-1 lg:order-1">
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-500 to-violet-500 text-white border-0 shadow-lg animate-bounce">
                <span className="w-6 h-6 bg-white text-purple-600 rounded-full flex items-center justify-center text-xs font-bold mr-2">3</span>
                Patient Experience
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                Patients Receive <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">Digital Forms</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Your patients receive secure, mobile-optimized consent forms via email. They can read, understand, and sign directly from any device with our intuitive interface - no app downloads required.
              </p>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <DevicePhoneMobileIcon className="h-6 w-6 text-purple-500 mr-3" />
                  <span className="text-gray-700 font-medium">Mobile-optimized interface</span>
                </div>
                <div className="flex items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <ShieldCheckIcon className="h-6 w-6 text-purple-500 mr-3" />
                  <span className="text-gray-700 font-medium">Secure digital signatures</span>
                </div>
                <div className="flex items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <UserIcon className="h-6 w-6 text-purple-500 mr-3" />
                  <span className="text-gray-700 font-medium">No account creation needed</span>
                </div>
              </div>
            </div>

            {/* Enhanced Mobile Mockup - Step 3 */}
            <div className="order-2 lg:order-2 relative">
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden transform hover:scale-105 transition-all duration-500 hover:shadow-3xl max-w-xs mx-auto">
                {/* Phone Frame */}
                <div className="relative">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-20 h-6 bg-gray-900 rounded-b-2xl z-10"></div>
                  
                  {/* Enhanced Screen Content */}
                  <div className="bg-gradient-to-br from-purple-500 to-violet-600 p-6 text-white">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <DocumentCheckIcon className="h-8 w-8 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold mb-2">Your consent</h4>
                      <p className="text-purple-100 text-sm">Review and sign</p>
                    </div>
                    
                    <div className="bg-white/10 rounded-xl p-4 mb-4 backdrop-blur-sm">
                      <p className="text-sm text-purple-100 mb-3 flex items-center">
                        <ShieldCheckIcon className="h-4 w-4 mr-2" />
                        Please review and sign below:
                      </p>
                      <div className="bg-white rounded-lg p-3 mb-3">
                        <p className="text-xs text-gray-600">I understand the risks and benefits...</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-600">Signature: <span className="font-semibold text-gray-800">John Smith</span></p>
                      </div>
                    </div>
                    
                    <button className="w-full bg-white text-purple-600 py-3 px-4 rounded-lg font-semibold text-sm hover:bg-gray-50 transition-colors duration-200 shadow-lg">
                      Sign & Submit
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Animated Connection Line */}
              <div className="hidden lg:block absolute left-1/2 bottom-0 transform -translate-x-1/2">
                <div className="w-1 h-20 bg-gradient-to-b from-purple-500 to-violet-500 animate-pulse"></div>
                <div className="w-4 h-4 bg-purple-500 rounded-full mx-auto -mt-2 animate-bounce"></div>
              </div>
            </div>
          </div>

          {/* Step 4: Track & Download */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center opacity-0 transform translate-y-10" data-lazy-load>
            {/* Enhanced Dashboard Mockup - Step 4 */}
            <div className="relative order-1 lg:order-1">
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden transform hover:scale-105 transition-all duration-500 hover:shadow-3xl">
                {/* Modern Browser Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex items-center space-x-3 border-b border-gray-200">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white rounded-xl px-4 py-2 text-sm text-gray-600 text-center shadow-sm border border-gray-200">
                      <span className="text-indigo-600 font-semibold">smile</span>consent.co.uk
                    </div>
                  </div>
                </div>
                
                {/* Enhanced Dashboard Content */}
                <div className="p-8">
                  <div className="mb-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                      <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-2" />
                      Consent Status Dashboard
                    </h4>
                    
                    {/* Enhanced Timeline */}
                    <div className="space-y-4">
                      <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-4">
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">Form Created</div>
                          <div className="text-xs text-gray-500">Today at 10:30 AM</div>
                        </div>
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      </div>
                      
                      <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mr-4">
                          <DocumentCheckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">Sent to Patient</div>
                          <div className="text-xs text-gray-500">Today at 10:35 AM</div>
                        </div>
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                      </div>
                      
                      <div className="flex items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mr-4">
                          <ClockIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">Patient Viewed</div>
                          <div className="text-xs text-gray-500">Today at 2:15 PM</div>
                        </div>
                        <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                      </div>
                      
                      <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-4">
                          <CheckCircleIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">Signed & Completed</div>
                          <div className="text-xs text-gray-500">Today at 2:20 PM</div>
                        </div>
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-6 border-t border-gray-200">
                    <button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-8 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl text-lg flex items-center justify-center">
                      <DocumentCheckIcon className="h-5 w-5 mr-2" />
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 order-2 lg:order-2">
              <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0 shadow-lg animate-bounce">
                <span className="w-6 h-6 bg-white text-indigo-600 rounded-full flex items-center justify-center text-xs font-bold mr-2">4</span>
                Progress Tracking
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                Monitor & <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Download</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Track your patient's consent journey in real-time with our comprehensive dashboard. Once completed, instantly download the signed form as a PDF or integrate directly with your practice management system.
              </p>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <ChartBarIcon className="h-6 w-6 text-indigo-500 mr-3" />
                  <span className="text-gray-700 font-medium">Real-time status tracking</span>
                </div>
                <div className="flex items-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <DocumentCheckIcon className="h-6 w-6 text-indigo-500 mr-3" />
                  <span className="text-gray-700 font-medium">Instant PDF download</span>
                </div>
                <div className="flex items-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <CogIcon className="h-6 w-6 text-indigo-500 mr-3" />
                  <span className="text-gray-700 font-medium">Practice software integration</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced CTA Section */}
          <div className="text-center mt-20 opacity-0 transform translate-y-10" data-lazy-load>
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
              {/* Animated Background Elements */}
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
                <div className="absolute top-20 right-20 w-16 h-16 bg-white/10 rounded-full animate-pulse delay-1000"></div>
                <div className="absolute bottom-10 left-20 w-12 h-12 bg-white/10 rounded-full animate-pulse delay-2000"></div>
                <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/10 rounded-full animate-pulse delay-500"></div>
              </div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-white/20 backdrop-blur-sm text-white border border-white/30 mb-6 animate-bounce">
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  Start Your Consent Journey Today
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Streamline Your <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">Consent Pathway</span>?
                </h3>
                <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                  Join hundreds of UK dental practices already using SmileConsent to ensure CQC compliance and improve patient care. Get started in minutes, not days.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <Link
                    to="/register"
                    className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl text-lg transform hover:scale-105 flex items-center justify-center"
                  >
                    <SparklesIcon className="h-5 w-5 mr-2" />
                    Start Free Trial
                  </Link>
                  <button
                    onClick={handleScheduleDemo}
                    className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 text-lg transform hover:scale-105 flex items-center justify-center"
                  >
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Schedule Demo
                  </button>
                </div>
                <div className="flex items-center justify-center space-x-6 text-blue-200 text-sm">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    No charge during trial
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    14-day free trial
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Cancel anytime
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    No long-term contracts
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-blue-500/20 text-blue-200 border border-blue-400/30 mb-6">
              <ShieldCheckIcon className="h-4 w-4 mr-2" />
              Enterprise-Grade Security
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Your Patient Data is Protected
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Built with security-first principles to ensure your dental practice meets the highest standards of data protection and patient privacy.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* SSL Security */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Secure HTTPS Connection</h3>
              <p className="text-slate-300">
                All data transmission is encrypted with SSL/TLS protocols. Your patients' information is protected with bank-level security standards.
              </p>
            </div>
            
            {/* DOB Verification */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6">
                <UserIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">DOB Verification Required</h3>
              <p className="text-slate-300">
                Patients must verify their date of birth before accessing consent forms. This ensures only authorized individuals can view sensitive medical information.
              </p>
            </div>
            
            {/* GDPR Compliance */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl flex items-center justify-center mb-6">
                <DocumentCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">GDPR-aware design</h3>
              <p className="text-slate-300">
                Built with UK data protection in mind. Your practice remains the controller and responsible for lawful processing, DPIAs, and professional obligations.
              </p>
            </div>
            
            {/* Audit Trails */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6">
                <ClipboardDocumentCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Activity records</h3>
              <p className="text-slate-300">
                Consent-related events are logged where the product supports it, to support your own governance — scope depends on plan and workflow.
              </p>
            </div>
            
            {/* Data Protection */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                <LockClosedIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Protected Patient Portal</h3>
              <p className="text-slate-300">
                Secure patient access with encrypted communications. Patients can only view their own consent forms through verified authentication.
              </p>
            </div>
            
            {/* UK Data Centers */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/10">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6">
                <BuildingOfficeIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">UK-Based Data Storage</h3>
              <p className="text-slate-300">
                All patient data is stored in UK-based secure data centers, ensuring compliance with UK data protection regulations.
              </p>
            </div>
          </div>
          
          {/* Security Trust Indicators */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-white/10 backdrop-blur-sm text-white border border-white/20 mb-6">
              <ShieldCheckIcon className="h-5 w-5 mr-2" />
              Built for UK dental practices
            </div>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Our security measures are designed for UK dental practices, helping protect patient data. Your practice remains responsible for meeting its legal and professional obligations.
            </p>
          </div>
        </div>
      </section>

      {/* Consent documentation & scrutiny */}
      <section className="py-24 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-white/20 text-white border border-white/30 mb-6">
              <ShieldCheckIcon className="h-4 w-4 mr-2" />
              Clearer consent records
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Better evidence of informed consent
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Organised consent materials and activity logs help you demonstrate what was shared with patients. This supports your wider quality systems — it does not replace them and does not guarantee any CQC outcome.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <ChartBarIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Visibility</h3>
              <p className="text-green-100">
                See pending and completed consents in one place where the product provides a dashboard.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Checklists (your policies)</h3>
              <p className="text-green-100">
                Use your own inspection preparation processes alongside stored consent records.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <DocumentTextIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Evidence of information given</h3>
              <p className="text-green-100">
                Signed materials and timestamps where captured — subject to your configuration and plan.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Governance support</h3>
              <p className="text-green-100">
                Consistent workflows make peer review and training easier — responsibility stays with the practice.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Why documentation matters</h3>
              <p className="text-green-100 mb-6">
                Inspectors often ask how consent was obtained and recorded. SmileConsent helps you present a coherent digital trail for consent managed through the platform. It does not perform automated CQC compliance scoring or replace your wider governance.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">5</div>
                  <div className="text-green-100">CQC key lines of enquiry (practice-led)</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">24/7</div>
                  <div className="text-green-100">Access when you need records</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">Edit</div>
                  <div className="text-green-100">Clinician review before send</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory Alignment & Legal Evidence */}
      <section className="py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg mb-6">
              <ShieldCheckIcon className="h-5 w-5 mr-2" />
              UK-focused product
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Designed for UK dental consent practice
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Smile Consent reflects common UK consent themes and workflows. Your practice remains responsible for meeting all applicable law, regulation, and professional standards.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Regulatory Alignment */}
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Regulatory Alignment</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <DocumentCheckIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">CQC Regulation 11</h4>
                  </div>
                  <p className="text-gray-600">Need for Consent - Ensures practices obtain valid consent before treatment delivery.</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <ShieldCheckIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">Montgomery v Lanarkshire [2015]</h4>
                  </div>
                  <p className="text-gray-600">UKSC 11 - Establishes duty to disclose material risks and treatment alternatives.</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                      <AcademicCapIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">GDC Standards</h4>
                  </div>
                  <p className="text-gray-600">Standards for the Dental Team - Professional standards for consent and patient communication.</p>
                </div>
              </div>
            </div>

            {/* Legal Evidence */}
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Legal & Regulatory Evidence</h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-xl p-6 text-white shadow-lg">
                  <h4 className="text-xl font-bold mb-3">Montgomery v Lanarkshire [2015]</h4>
                  <p className="text-red-100 mb-4">
                    "The doctor is under a duty to take reasonable care to ensure that the patient is aware of any material risks..."
                  </p>
                  <div className="text-sm text-red-200">UKSC 11</div>
                </div>

                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-6 text-white shadow-lg">
                  <h4 className="text-xl font-bold mb-3">GDC Standards for the Dental Team</h4>
                  <p className="text-blue-100 mb-4">
                    "You must make sure that patients give their consent to treatment before it starts."
                  </p>
                  <div className="text-sm text-blue-200">Principle 3</div>
                </div>

                <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg">
                  <h4 className="text-xl font-bold mb-3">Dental Law Partnership</h4>
                  <p className="text-orange-100">
                    Frequently reports compensation claims where consent was not fully documented or properly explained.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Common Risks Prevented */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Legal and Compliance Risks Prevented</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-xl bg-red-50 border border-red-200">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <XMarkIcon className="h-6 w-6 text-red-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">No Written Record</h4>
                <p className="text-sm text-gray-600">Prevents missing consent documentation</p>
              </div>

              <div className="text-center p-6 rounded-xl bg-orange-50 border border-orange-200">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Failure to Disclose</h4>
                <p className="text-sm text-gray-600">Ensures material risks are disclosed</p>
              </div>

              <div className="text-center p-6 rounded-xl bg-yellow-50 border border-yellow-200">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Time Pressure</h4>
                <p className="text-sm text-gray-600">Prevents rushed consent processes</p>
              </div>

              <div className="text-center p-6 rounded-xl bg-blue-50 border border-blue-200">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Documentation Issues</h4>
                <p className="text-sm text-gray-600">Ensures proper audit trails</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Smile Consent Section */}
      <section className="py-24 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg mb-6">
              <DocumentCheckIcon className="h-5 w-5 mr-2" />
              💼 Why Smile Consent?
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Consent isn't just a form — it's a legal, ethical, and CQC requirement
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Smile Consent gives dental practices a structured, digital way to manage, record, and prove valid patient consent.
            </p>
          </div>

          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">
              ✅ How Smile Consent Supports Your CQC Inspection:
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* SAFE */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-red-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <ShieldCheckIcon className="h-6 w-6 text-red-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🛡️ SAFE</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Ensures clear, documented consent before any treatment</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Helps prevent wrong procedures or misunderstandings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Integrated capacity checks for vulnerable patients</span>
                </li>
              </ul>
            </div>

            {/* EFFECTIVE */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-blue-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                  <ChartBarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🎯 EFFECTIVE</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Ensures consent is based on accurate, relevant information</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Supports clinical decision-making with AI-generated, up-to-date consent</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Reinforces best practice from GDC and Mental Capacity Act guidance</span>
                </li>
              </ul>
            </div>

            {/* CARING */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-pink-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mr-4">
                  <UserGroupIcon className="h-6 w-6 text-pink-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">❤️ CARING</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Provides accessible consent formats (easy-read, translations)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Builds trust with clear, compassionate communication</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Involves families/advocates when appropriate</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom row - 2 wide cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* RESPONSIVE */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-green-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                  <ClockIcon className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🔄 RESPONSIVE</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Customizable to each patient and treatment plan</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Digital forms can be adapted quickly for urgent or complex cases</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Includes options for remote consent and pre-visit completion</span>
                </li>
              </ul>
            </div>

            {/* WELL-LED */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-purple-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                  <BuildingOfficeIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">🏁 WELL-LED</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Tracks who obtained consent, when, and how — fully auditable</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Ensures consistency across clinicians and staff</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Supports staff training and governance requirements</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="text-center">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-8 text-white shadow-xl">
                <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Consent Process?</h3>
                <p className="text-indigo-100 mb-6">
                  Join hundreds of UK dental practices already using Smile Consent to ensure CQC compliance and improve patient care.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/register"
                    className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 shadow-lg"
                  >
                    Start Free Trial
                  </Link>
                  <button
                    onClick={handleScheduleDemo}
                    className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors duration-200"
                  >
                    Schedule Demo
                  </button>
                </div>
              </div>
            </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg mb-6">
              <SparklesIcon className="h-5 w-5 mr-2" />
              Core Features
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need for Digital Consent
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Comprehensive features designed specifically for UK dental practices to ensure compliance and improve patient care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Procedure-Specific Forms</h3>
              <p className="text-gray-600 mb-4">Digital consent forms for fillings, extractions, whitening, implants, and more.</p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Review and edit every section before sending</li>
                <li>• Risk disclosure included</li>
                <li>• Alternative treatments listed</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <DocumentCheckIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Digital Signature Capture</h3>
              <p className="text-gray-600 mb-4">Secure digital signatures with timestamped records for legal compliance.</p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Legally binding signatures</li>
                <li>• Timestamp verification</li>
                <li>• Audit trail included</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheckIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Secure Cloud Storage</h3>
              <p className="text-gray-600 mb-4">Cloud-based storage with comprehensive audit trails and data protection.</p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• GDPR compliant</li>
                <li>• Encrypted storage</li>
                <li>• Backup and recovery</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mb-6">
                <UserIcon className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">User-Friendly Interface</h3>
              <p className="text-gray-600 mb-4">Intuitive design for in-practice use on tablets or desktop computers.</p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Touch-friendly design</li>
                <li>• Quick setup process</li>
                <li>• Minimal training required</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-2xl flex items-center justify-center mb-6">
                <DocumentTextIcon className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">PDF Export & Printing</h3>
              <p className="text-gray-600 mb-4">Printable and exportable PDFs for patient records and practice documentation.</p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Professional formatting</li>
                <li>• Practice branding</li>
                <li>• Easy integration</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <ChartBarIcon className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Centralized Dashboard</h3>
              <p className="text-gray-600 mb-4">Monitor consent activity and staff usage with comprehensive analytics.</p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Real-time monitoring</li>
                <li>• Staff accountability</li>
                <li>• Performance insights</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">Built-in Best Practice Guides</h3>
            <p className="text-emerald-100 mb-6">
              Comprehensive guides for effective communication during consent processes, ensuring patients fully understand their treatment options.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-emerald-200 mr-3" />
                <span>Consent and patient communication</span>
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-emerald-200 mr-3" />
                <span>Risk explanation guides</span>
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-emerald-200 mr-3" />
                <span>Patient education resources</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-gray-600 to-gray-700 text-white shadow-lg mb-6">
              <StarIcon className="h-5 w-5 mr-2" />
              Benefits for Your Practice
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Transform Your Consent Process
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Join hundreds of UK dental practices already using Smile Consent to ensure compliance and improve patient care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheckIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Peace of Mind</h3>
              <p className="text-gray-600">Confidence during CQC inspections with comprehensive compliance documentation.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                <DocumentCheckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Legal Defence</h3>
              <p className="text-gray-600">Stronger legal defence if complaints arise with complete audit trails.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mb-6">
                <UserGroupIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Consistent Communication</h3>
              <p className="text-gray-600">More consistent patient communication across all clinicians and staff.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl flex items-center justify-center mb-6">
                <StarIcon className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Professional Experience</h3>
              <p className="text-gray-600">A more professional, modern patient experience that builds trust.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Easy Staff Onboarding</h3>
              <p className="text-gray-600 mb-6">Simplified training and accountability for new staff members with intuitive workflows.</p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Minimal training required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Standardized processes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Clear accountability</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Who It's For</h3>
              <p className="text-gray-600 mb-6">Perfect for all types of UK dental practices and clinics.</p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>NHS and private dental practices</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Cosmetic and implant clinics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Orthodontists and general dental teams</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Practices preparing for CQC inspections</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-white/20 text-white border border-white/30 mb-6">
              <DocumentCheckIcon className="h-4 w-4 mr-2" />
              UK Dental Expertise
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Built for UK Dental Practices
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              SmileConsent is built for UK dental consent workflows — AI-assisted drafting with mandatory clinician review, plus signing and records to support your documentation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">GDC-oriented wording</h3>
              <p className="text-blue-100">
                Drafts reflect common professional expectations; you must still exercise independent judgment for each patient.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <ChartBarIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Inspection preparation support</h3>
              <p className="text-blue-100">
                Retrieve consent records from the platform to support your own preparation — not a guarantee of inspection outcomes.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <GlobeAltIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">NHS & Private</h3>
              <p className="text-blue-100">
                Seamless support for both NHS treatment bands and private procedures with specialized workflows.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <ClockIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Time Saving</h3>
              <p className="text-blue-100">
                AI-generated consent and streamlined workflow save practices hours daily on consent management.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Why UK Dental Practices Choose SmileConsent</h3>
              <p className="text-blue-100">
                Unlike generic consent solutions, SmileConsent understands the unique challenges of UK dental practices.
                Our platform combines regulatory expertise with practical workflow automation to deliver a solution
                that actually works for UK dentists and their patients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 bg-white border-t border-gray-100 comparison-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">What SmileConsent Includes</h2>
            <p className="text-base sm:text-lg text-gray-600 px-2">Core features designed for UK dental consent workflows</p>
          </div>
          
          {/* Mobile-friendly feature cards */}
          <div className="space-y-3 sm:hidden comparison-cards">
            {featureComparison.map((row, idx) => (
              <div key={idx} className={`bg-white rounded-xl shadow border border-gray-200 p-3 sm:p-4 ${idx % 2 === 0 ? 'bg-gray-50' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-semibold text-xs sm:text-sm leading-tight text-gray-800 flex-1">{row.feature}</h3>
                  {row.included && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" aria-label="Included" />
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop table - hidden on mobile */}
          <div className="hidden sm:block">
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow">
              <table className="w-full bg-white">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 sm:px-6 py-4 text-left text-gray-700 font-semibold text-sm sm:text-base">Feature</th>
                    <th className="px-4 sm:px-6 py-4 text-center text-blue-700 font-semibold text-sm sm:text-base w-32">Included</th>
                  </tr>
                </thead>
                <tbody>
                  {featureComparison.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 sm:px-6 py-4 text-gray-800 font-medium text-sm sm:text-base">
                        <div className="break-words max-w-[500px]">{row.feature}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-center">
                        {row.included ? (
                          <CheckCircleIcon className="h-6 w-6 text-green-500 mx-auto" aria-label="Included" />
                        ) : (
                          <span className="inline-block bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-semibold text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>



      {/* Pricing Section */}
      <section ref={pricingRef} id="pricing-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20 pricing-animate">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that scales with your practice
            </p>
            <p className="text-sm text-gray-500 mt-3 max-w-2xl mx-auto">
              All plans include unlimited clinicians — no per-seat fees.
            </p>
          </div>

          {/* Remove Billing Cycle Toggle */}
          {/* <div className="flex justify-center mb-12">
            <div className="bg-gray-100 rounded-full p-1 flex items-center">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 relative ${
                  billingCycle === 'annual'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                  Save 17%
                </span>
              </button>
            </div>
          </div> */}
          
          {/* Free Trial Banner */}
          <div className="mb-8 text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-center space-x-3">
                <span className="text-2xl">🎉</span>
                <div>
                  <div className="text-xl font-bold">14-Day Free Trial - No Charge During Trial!</div>
                  <div className="text-sm opacity-90">Try SmileConsent risk-free. Your card won't be charged until after your trial ends. Cancel anytime — no long-term contracts.</div>
                </div>
                <span className="text-2xl">🎉</span>
              </div>
            </div>
          </div>

          <PricingComparisonCards animate cardClassName="pricing-animate" />

          {/* Pricing FAQ */}
          <div className="mt-16 text-center">
            <p className="text-gray-600 mb-4">
              All plans include a 14-day free trial • Cancel anytime • No long-term contracts • No setup fees
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-gray-500">
              <span>✓ Cancel anytime</span>
              <span>✓ No long-term contracts</span>
              <span>✓ 24/7 support</span>
              <span>✓ GDPR compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Dental Practice?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join UK dental practices using SmileConsent to streamline consent with AI-assisted drafts and clinician-reviewed sending.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-10 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Start Your Free Trial
            </Link>
            <button
              onClick={handleScheduleDemo}
              className="group border-2 border-white text-white hover:bg-white hover:text-blue-600 px-10 py-4 rounded-xl text-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <CalendarIcon className="h-5 w-5 group-hover:animate-pulse" />
              <span>Book A Demo</span>
            </button>
          </div>
          <p className="mt-6 text-blue-100">
            Cancel anytime • No long-term contracts • Implementation in under 24 hours • UK-based support
          </p>
        </div>
      </section>

      {/* Internal Navigation Section for SEO */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InternalLinks 
            title="Explore Our Complete Dental Practice Solutions"
            links={[
              {
                title: "Digital Consent Forms",
                description: "Professional e-signature consent forms",
                path: "/digital-consent-dentists",
                icon: <DocumentCheckIcon className="w-6 h-6" />
              },
              {
                title: "Implant consent form UK",
                description: "Treatment-specific consent for implant cases",
                path: "/implant-consent-form-uk",
                icon: <ClipboardDocumentListIcon className="w-6 h-6" />
              },
              {
                title: "Orthodontic consent form UK",
                description: "Digital consent wording for braces and aligners",
                path: "/orthodontic-consent-form-uk",
                icon: <ChartBarIcon className="w-6 h-6" />
              },
              {
                title: "Tooth extraction consent form UK",
                description: "Consent for routine and surgical extraction",
                path: "/tooth-extraction-consent-form-uk",
                icon: <ShieldCheckIcon className="w-6 h-6" />
              },
              {
                title: "Root canal consent form UK",
                description: "Endodontic consent with risks and alternatives",
                path: "/root-canal-consent-form-uk",
                icon: <BuildingOfficeIcon className="w-6 h-6" />
              },
              {
                title: "Pricing Plans",
                description: "Affordable plans for all practice sizes",
                path: "/dental-consent-plans",
                icon: <QuestionMarkCircleIcon className="w-6 h-6" />
              },
              {
                title: "Consent by procedure",
                description: "AI-generated dental consent for any procedure",
                path: "/dental-templates",
                icon: <AcademicCapIcon className="w-6 h-6" />
              }
            ]}
          />
        </div>
      </section>

      {/* Footer */}
      <footer ref={footerRef} className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <DocumentCheckIcon className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold">SmileConsent</span>
              </div>
              <p className="text-gray-400 text-sm">
                AI-assisted dental consent for UK practices with clinician review and digital signing.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Dental Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
                <li><Link to="/dental-consent-software" className="text-gray-400 hover:text-white text-sm">Consent Software</Link></li>
                <li><Link to="/dental-google-reviews" className="text-gray-400 hover:text-white text-sm">Google Reviews</Link></li>
                <li><Link to="/dental-practice-management" className="text-gray-400 hover:text-white text-sm">Practice Management</Link></li>
                <li><Link to="/dental-templates" className="text-gray-400 hover:text-white text-sm">Generate consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><Link to="/cpd-legal-consent" className="text-gray-400 hover:text-white text-sm">Free CPD Course</Link></li>
                <li><Link to="/dental-consent-guide" className="text-gray-400 hover:text-white text-sm">Consent Guide</Link></li>
                <li><Link to="/dental-consent-law-uk" className="text-gray-400 hover:text-white text-sm">Consent Law UK</Link></li>
                <li><Link to="/support" className="text-gray-400 hover:text-white text-sm">Support</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 text-xs sm:text-sm text-gray-400">
              <span>© {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</span>
              <span className="hidden sm:inline">•</span>
              <span>UK-based support</span>
              <span className="hidden sm:inline">•</span>
              <span>Clinician-reviewed sends</span>
              <span className="hidden sm:inline">•</span>
              <span>UK-oriented workflows</span>
            </div>
            <div className="mt-4">
              <CompanyDisclosure tone="dark" showCopyright={false} />
            </div>
          </div>
        </div>
      </footer>

      {/* Launch Modal */}
      {showLaunchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-50"></div>
            
            {/* Close Button */}
            <button
              onClick={handleLaunchModalClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors z-10"
            >
              ×
            </button>

            {/* Content */}
            <div className="relative z-10 p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SparklesIcon className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  🚀 Coming Soon to the UK!
                </h2>
                <p className="text-gray-600">
                  SmileConsent is launching in the UK soon. Be among the first to experience the future of dental consent management.
                </p>
              </div>

              {/* Benefits */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-3">Why Register Early?</h3>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                    Priority access when we launch
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                    Exclusive early adopter benefits
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                    Free consultation with our UK team
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                    Special launch pricing
                  </li>
                </ul>
              </div>

              {/* Form */}
              <form onSubmit={handleLaunchModalSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={launchFormData.name}
                    onChange={(e) => setLaunchFormData({...launchFormData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Dr. Sarah Johnson"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={launchFormData.email}
                    onChange={(e) => setLaunchFormData({...launchFormData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="sarah@dentalpractice.co.uk"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={launchFormData.phone}
                    onChange={(e) => setLaunchFormData({...launchFormData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="020 7123 4567"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLaunchSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-blue-400 disabled:to-indigo-400 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {isLaunchSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Registering...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <SparklesIcon className="h-5 w-5" />
                      <span>Register My Interest</span>
                    </div>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="text-center mt-6">
                <p className="text-xs text-gray-500">
                  We'll notify you as soon as we launch in the UK
                </p>
                <button
                  onClick={handleLaunchModalClose}
                  className="text-sm text-gray-500 hover:text-gray-700 mt-2 underline"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Capacity Assessment Test Modal */}
      {showCapacityTest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-50 via-pink-50 to-indigo-50 border-b border-purple-200 px-8 py-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <CpuChipIcon className="h-7 w-7 text-white" />
                    </div>
                    {/* Animated glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 rounded-2xl blur-lg opacity-50 animate-pulse"></div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent">
                      Mental Capacity Assessment
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">UK Mental Capacity Act 2005 Compliant</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCapacityTest(false)}
                  className="relative group p-2 rounded-lg hover:bg-white/50 transition-all duration-200"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-400 rounded-lg opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                  <XMarkIcon className="h-6 w-6 text-gray-500 group-hover:text-gray-700 relative z-10 transition-colors" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-8 py-6">
              {!capacityTestResults ? (
                <div>
                  {/* Progress Bar */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700 bg-white px-3 py-1 rounded-full shadow-sm">
                        Question {capacityTestStep + 1} of {capacityTestQuestions.length}
                      </span>
                      <span className="text-sm font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                        {Math.round(((capacityTestStep + 1) / capacityTestQuestions.length) * 100)}% Complete
                      </span>
                    </div>
                    <div className="relative w-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-full h-3 shadow-inner">
                      <div 
                        className="bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 h-3 rounded-full transition-all duration-500 ease-out shadow-lg"
                        style={{ width: `${((capacityTestStep + 1) / capacityTestQuestions.length) * 100}%` }}
                      >
                        {/* Animated shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* Question */}
                  <div className="mb-8">
                    <div className="flex items-center mb-6">
                      <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 text-white rounded-2xl flex items-center justify-center text-lg font-bold mr-4 shadow-lg">
                          {capacityTestStep + 1}
                        </div>
                        {/* Glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 rounded-2xl blur-md opacity-50 animate-pulse"></div>
                      </div>
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent">
                        {capacityTestQuestions[capacityTestStep].title}
                      </h3>
                    </div>
                    
                    <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-indigo-50 border border-purple-200 rounded-2xl p-6 mb-6 shadow-lg">
                      <p className="text-xl text-gray-800 font-semibold mb-4">
                        {capacityTestQuestions[capacityTestStep].question}
                      </p>
                      
                      <div className="bg-white/80 border border-purple-200 rounded-xl p-4">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <InformationCircleIcon className="h-4 w-4 text-white" />
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {capacityTestQuestions[capacityTestStep].description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Answer Options */}
                    <div className="space-y-4">
                      {capacityTestQuestions[capacityTestStep].options.map((option, index) => (
                        <label
                          key={option.value}
                          className={`group relative flex items-start p-6 border-2 rounded-2xl cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${
                            capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value
                              ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 shadow-lg shadow-purple-500/25'
                              : 'border-gray-200 hover:border-purple-300 bg-white hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-pink-50/50'
                          }`}
                        >
                          {/* Animated background */}
                          <div className={`absolute inset-0 rounded-2xl transition-opacity duration-300 ${
                            capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value
                              ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 opacity-100'
                              : 'bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100'
                          }`}></div>
                          
                          {/* Radio button with custom styling */}
                          <div className="relative z-10 mr-4 mt-1">
                            <input
                              type="radio"
                              name={`question-${capacityTestQuestions[capacityTestStep].id}`}
                              value={option.value}
                              checked={capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value}
                              onChange={() => handleCapacityTestAnswer(capacityTestQuestions[capacityTestStep].id, option.value)}
                              className="sr-only"
                            />
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                              capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value
                                ? 'border-purple-500 bg-purple-500'
                                : 'border-gray-300 group-hover:border-purple-400'
                            }`}>
                              {capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value && (
                                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                              )}
                            </div>
                          </div>
                          
                          {/* Option text */}
                          <div className="relative z-10 flex-1">
                            <span className={`text-lg font-medium transition-colors duration-300 ${
                              capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value
                                ? 'text-purple-700'
                                : 'text-gray-700 group-hover:text-gray-900'
                            }`}>
                              {option.label}
                            </span>
                          </div>
                          
                          {/* Selection indicator */}
                          {capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] === option.value && (
                            <div className="absolute top-4 right-4">
                              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                                <CheckCircleIcon className="h-5 w-5 text-white" />
                              </div>
                            </div>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex justify-between">
                    <button
                      onClick={handleCapacityTestPrevious}
                      disabled={capacityTestStep === 0}
                      className={`relative group px-8 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                        capacityTestStep === 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 hover:from-gray-300 hover:to-gray-400 hover:scale-105 shadow-lg hover:shadow-xl'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>Previous</span>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleCapacityTestNext}
                      disabled={!capacityTestAnswers[capacityTestQuestions[capacityTestStep].id]}
                      className={`relative group px-8 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                        !capacityTestAnswers[capacityTestQuestions[capacityTestStep].id]
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 text-white hover:from-purple-600 hover:via-pink-600 hover:to-indigo-600 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{capacityTestStep === capacityTestQuestions.length - 1 ? 'Get Results' : 'Next'}</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      
                      {/* Animated background */}
                      {capacityTestAnswers[capacityTestQuestions[capacityTestStep].id] && (
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                /* Results */
                <div>
                  <div className="text-center mb-8">
                    <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                      capacityTestResults.percentage >= 80 
                        ? 'bg-green-100' 
                        : capacityTestResults.percentage >= 60 
                        ? 'bg-yellow-100' 
                        : 'bg-red-100'
                    }`}>
                      <ClipboardDocumentCheckIcon className={`h-10 w-10 ${
                        capacityTestResults.percentage >= 80 
                          ? 'text-green-600' 
                          : capacityTestResults.percentage >= 60 
                          ? 'text-yellow-600' 
                          : 'text-red-600'
                      }`} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {capacityTestResults.capacity}
                    </h3>
                    <p className="text-lg text-gray-600 mb-4">
                      {capacityTestResults.recommendation}
                    </p>
                    <div className="bg-gray-100 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-center space-x-4">
                        <span className="text-sm text-gray-600">Score:</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {capacityTestResults.score}/{capacityTestResults.maxScore}
                        </span>
                        <span className="text-sm text-gray-600">
                          ({Math.round(capacityTestResults.percentage)}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Recommendations:</h4>
                    <div className="space-y-3">
                      {capacityTestResults.details.map((detail, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Legal Information */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-blue-900 mb-2">Legal Framework</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      This assessment is based on the Mental Capacity Act 2005 (England and Wales) and follows the 
                      two-stage test for capacity assessment:
                    </p>
                    <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                      <li><strong>Stage 1:</strong> Does the person have an impairment or disturbance in the functioning of their mind or brain?</li>
                      <li><strong>Stage 2:</strong> Does that impairment or disturbance mean they are unable to make the specific decision?</li>
                    </ol>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={resetCapacityTest}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                    >
                      Take Test Again
                    </button>
                    <button
                      onClick={() => setShowCapacityTest(false)}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Popup */}
      {showCookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 p-3 sm:p-4 md:p-6 mobile-optimized">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 sm:gap-4">
              {/* Cookie Icon and Content */}
              <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheckIconSolid className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    We value your privacy
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3">
                    This website stores cookies on your computer. These cookies are used to collect information about how you interact with our website and allow us to remember you. We use this information in order to improve and customize your browsing experience and for analytics and metrics about our visitors both on this website and other media.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 text-xs text-gray-500">
                    <span>🔒 GDPR compliant • 🛡️ Secure data handling</span>
                    <span>📊 Analytics only • 🍪 Essential cookies</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={handleCookieDecline}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all duration-200 text-sm flex items-center justify-center space-x-2"
                >
                  <XMarkIcon className="h-4 w-4" />
                  <span>Decline</span>
                </button>
                <button
                  onClick={handleCookieAccept}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm flex items-center justify-center space-x-2"
                >
                  <ShieldCheckIcon className="h-4 w-4" />
                  <span>Accept All</span>
                </button>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs text-gray-500">
                <div className="flex flex-wrap gap-2 sm:gap-4 text-xs">
                  <span>If you decline, your information won't be tracked when you visit this website.</span>
                  <span>A single cookie will be used in your browser to remember your preference not to be tracked.</span>
                </div>
                <Link 
                  to="/privacy-policy" 
                  className="text-blue-600 hover:text-blue-700 font-medium underline flex items-center space-x-1 text-xs sm:text-sm"
                >
                  <DocumentCheckIcon className="h-3 w-3" />
                  <span>Privacy Policy</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live Activity Feed - Bottom of Screen - DISABLED */}
      {false && (
      <div className="fixed bottom-4 left-4 right-4 z-30 max-w-md mx-auto">
        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-2xl p-4 transform transition-all duration-500 hover:scale-105">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Live Activity</span>
            </div>
            <div className="text-xs text-gray-500">
              {liveActivities[currentActivity].time}
            </div>
          </div>
          <div className="text-sm text-gray-800">
            <span className="font-semibold text-blue-600">{liveActivities[currentActivity].initials}</span>
            <span className="text-gray-600"> from </span>
            <span className="font-medium text-gray-800">{liveActivities[currentActivity].city}</span>
            <span className="text-gray-600"> {liveActivities[currentActivity].action}</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              🎯 Join {liveActivities.length} other practices
            </div>
            <Link
              to="/register"
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg font-medium transition-colors duration-200"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </div>
      )}

    </div>
    </>
  );
}
